import { useCallback, useEffect, useRef, useState } from 'react';
import { useVideoPlayerContext } from '@/components/VideoPlayer/VideoPlayerProvider';

const BUFFERING_INDICATOR_DELAY_MS = 250;

export function useVideoBuffering(): boolean {
    const { emitter, streaming, loopTimeRange } = useVideoPlayerContext();
    const [isBuffering, setIsBuffering] = useState(false);
    const showTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastWindowRef = useRef<string | null>(null);

    const cancelScheduledShow = useCallback(() => {
        if (!showTimeoutRef.current) return;
        clearTimeout(showTimeoutRef.current);
        showTimeoutRef.current = null;
    }, []);

    const clearBuffering = useCallback(() => {
        cancelScheduledShow();
        setIsBuffering(false);
    }, [cancelScheduledShow]);

    const windowKey = loopTimeRange
        ? `${loopTimeRange.startTime}-${loopTimeRange.endTime}`
        : null;

    useEffect(() => {
        const scheduleShow = () => {
            if (showTimeoutRef.current) return;
            showTimeoutRef.current = setTimeout(() => {
                showTimeoutRef.current = null;
                setIsBuffering(true);
            }, BUFFERING_INDICATOR_DELAY_MS);
        };

        const onSourceChange = () => {
            if (windowKey !== null && windowKey === lastWindowRef.current) {
                return;
            }
            lastWindowRef.current = windowKey;
            scheduleShow();
        };

        const unsubscribes = [
            emitter.on('seeking', scheduleShow),
            emitter.on('waiting', scheduleShow),
            emitter.on('sourcechange', onSourceChange),
            emitter.on('seeked', clearBuffering),
            emitter.on('playing', clearBuffering),
            emitter.on('loadeddata', clearBuffering),
            emitter.on('error', clearBuffering),
            emitter.on('ended', clearBuffering),
        ];

        return () => {
            unsubscribes.forEach((unsubscribe) => unsubscribe());
            cancelScheduledShow();
        };
    }, [emitter, cancelScheduledShow, clearBuffering, windowKey]);

    useEffect(() => {
        clearBuffering();
        return cancelScheduledShow;
    }, [streaming.live, cancelScheduledShow, clearBuffering]);

    return isBuffering && !streaming.live;
}
